/**
 * User-created studies for Study Design — persisted in localStorage until the
 * data-service catalog is wired into this UI.
 */

import { FlaskConical, type LucideIcon } from 'lucide-react'
import { formatOdmPatchSegment } from '@/lib/odm/study-version-meta'
import { reconcileStudyOdmVersion } from '@/lib/odm/reconcile-study-version'
import type { StudyDesignStudy, StudyStatus } from '@/components/study-design/mock-data'

export const USER_STUDIES_UPDATED = 'crucible:user-studies-updated'

type StoredUserStudy = {
  id: string
  slug: string
  code: string
  title: string
  phase: string
  status: StudyStatus
  currentVersionId: string
  versions: StudyDesignStudy['versions']
  forms: number
  items: number
  lastActiveAt: string
  stepIdx: number
  createdAt: string
  sessionId?: string
  /** True once persisted to the data-service via saveToCloud (opt-in). */
  cloud?: boolean
}

const USER_STUDY_ICON: LucideIcon = FlaskConical

function registryKey(userId: string): string {
  return `crucible_user_studies_${userId}`
}

function loadRaw(userId: string): StoredUserStudy[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(registryKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as StoredUserStudy[]
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function saveRaw(userId: string, studies: StoredUserStudy[]): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(registryKey(userId), JSON.stringify(studies))
  window.dispatchEvent(new CustomEvent(USER_STUDIES_UPDATED))
}

function toStudyDesignStudy(stored: StoredUserStudy): StudyDesignStudy {
  return { ...stored, Icon: USER_STUDY_ICON }
}

/**
 * A study name is a code, not a paragraph. A 600-character chat message once
 * became the title, the URL slug AND the version string (the slug feeds
 * `currentVersionId`), breaking the sidebar layout and flirting with URL
 * limits. The dialog input carries the same cap; this is the backstop for
 * every other caller.
 */
export const MAX_STUDY_NAME_LENGTH = 80
export const MAX_STUDY_SLUG_LENGTH = 60

export function slugifyStudyCode(code: string): string {
  const base = code
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_STUDY_SLUG_LENGTH)
    .replace(/-+$/, '')
  return base || 'study'
}

function ensureUniqueSlug(userId: string, base: string): string {
  const existing = new Set(loadRaw(userId).map(s => s.slug))
  if (!existing.has(base)) return base
  let i = 2
  while (existing.has(`${base}-${i}`)) i += 1
  return `${base}-${i}`
}

export function listUserStudies(userId: string | null | undefined): StudyDesignStudy[] {
  if (!userId) return []
  return loadRaw(userId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map(toStudyDesignStudy)
}

export function getUserStudyBySlug(
  userId: string | null | undefined,
  slug: string,
): StudyDesignStudy | undefined {
  if (!userId) return undefined
  const stored = loadRaw(userId).find(s => s.slug === slug)
  return stored ? toStudyDesignStudy(stored) : undefined
}

export interface CreateUserStudyInput {
  code: string
  phase: string
  title?: string
  sessionId?: string
}

export function createUserStudy(
  userId: string | null | undefined,
  input: CreateUserStudyInput,
): StudyDesignStudy | null {
  if (!userId) return null

  const trimmed = input.code.trim().slice(0, MAX_STUDY_NAME_LENGTH).trim()
  if (!trimmed) return null

  const slug = ensureUniqueSlug(userId, slugifyStudyCode(trimmed))
  const normalizedCode = trimmed.toUpperCase()
  const versionNumber = `1.0.${formatOdmPatchSegment()}.1`
  const versionId = `${slug}-v${versionNumber.replace(/\./g, '-')}`
  const now = new Date().toISOString()

  const stored: StoredUserStudy = {
    id: `user-${slug}`,
    slug,
    code: normalizedCode,
    title: input.title?.trim().slice(0, MAX_STUDY_NAME_LENGTH) || normalizedCode,
    phase: input.phase,
    status: 'draft',
    currentVersionId: versionId,
    stepIdx: 1,
    forms: 0,
    items: 0,
    lastActiveAt: 'Just now',
    createdAt: now,
    sessionId: input.sessionId,
    cloud: false,
    versions: [
      {
        id: versionId,
        versionNumber,
        status: 'draft',
        createdAt: now,
        createdBy: 'You',
        changeSummary: 'Study workspace created',
      },
    ],
  }

  saveRaw(userId, [stored, ...loadRaw(userId)])
  return toStudyDesignStudy(stored)
}

/**
 * Bring a study's recorded version in line with the live ODM's
 * `Study/@VersionID`, which is authoritative — it is the number Anvil
 * consumes. Without this the sidebar shows a stamp frozen at creation while
 * the workspace header shows the build stamp, and the two drift apart.
 *
 * Returns true only when something was written, so callers can rely on the
 * no-op case being silent: `saveRaw` dispatches USER_STUDIES_UPDATED, which
 * reloads the nav provider, so an unconditional write would loop.
 */
export function recordUserStudyOdmVersion(
  userId: string | null | undefined,
  slug: string | null | undefined,
  odmVersionId: string | null | undefined,
): boolean {
  if (!userId || !slug) return false

  const studies = loadRaw(userId)
  const idx = studies.findIndex(s => s.slug === slug)
  if (idx < 0) return false

  const stored = studies[idx]!
  const patch = reconcileStudyOdmVersion(stored, odmVersionId)
  if (!patch) return false

  const next = [...studies]
  next[idx] = { ...stored, ...patch }
  saveRaw(userId, next)
  return true
}

export function bindUserStudySession(
  userId: string | null | undefined,
  slug: string,
  sessionId: string,
): void {
  if (!userId || !sessionId.startsWith('session_')) return
  const studies = loadRaw(userId)
  const idx = studies.findIndex(s => s.slug === slug)
  if (idx < 0) return
  studies[idx] = { ...studies[idx]!, sessionId }
  saveRaw(userId, studies)
}

export function getUserStudyBySessionId(
  userId: string | null | undefined,
  sessionId: string,
): StudyDesignStudy | undefined {
  if (!userId) return undefined
  const stored = loadRaw(userId).find(s => s.sessionId === sessionId)
  return stored ? toStudyDesignStudy(stored) : undefined
}

const STUDIES_ENDPOINT = '/api/studies'

/**
 * Manual opt-in: POST a locally-created study to the data-service. On success,
 * mark the local entry cloud:true (adopting the server-returned id if it
 * differs) and notify listeners. On failure, leave the entry local and throw.
 */
export async function saveToCloud(
  userId: string | null | undefined,
  studyId: string,
): Promise<StudyDesignStudy> {
  if (!userId) throw new Error('saveToCloud: missing userId')

  const studies = loadRaw(userId)
  const idx = studies.findIndex(s => s.id === studyId)
  if (idx < 0) throw new Error(`saveToCloud: study ${studyId} not found`)

  const res = await fetch(STUDIES_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(studies[idx]!),
  })
  if (!res.ok) {
    throw new Error(`saveToCloud: server responded ${res.status}`)
  }

  const saved = (await res.json().catch(() => ({}))) as Partial<StoredUserStudy>
  const serverId = typeof saved.id === 'string' && saved.id ? saved.id : studies[idx]!.id
  const updated: StoredUserStudy = { ...studies[idx]!, id: serverId, cloud: true }
  studies[idx] = updated
  saveRaw(userId, studies)
  return toStudyDesignStudy(updated)
}

/**
 * Async listing that merges cloud studies (from the data-service) with
 * localStorage-only entries not yet saved, deduped by id (cloud wins). Falls
 * back to localStorage only when the fetch fails (e.g. offline). The existing
 * sync listUserStudies remains for callers that need synchronous access.
 */
export async function listStudiesMerged(
  userId: string | null | undefined,
): Promise<StudyDesignStudy[]> {
  if (!userId) return []

  const local = loadRaw(userId)

  let cloud: StoredUserStudy[] = []
  try {
    const res = await fetch(STUDIES_ENDPOINT)
    if (res.ok) {
      const data = await res.json()
      if (Array.isArray(data)) {
        cloud = (data as StoredUserStudy[]).map(s => ({ ...s, cloud: true }))
      }
    }
  } catch {
    // Offline / server unreachable — fall back to localStorage only.
    cloud = []
  }

  const byId = new Map<string, StoredUserStudy>()
  for (const s of local) byId.set(s.id, s)
  // Cloud wins on id conflicts.
  for (const s of cloud) byId.set(s.id, s)

  return Array.from(byId.values())
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .map(toStudyDesignStudy)
}
